import './PhotosPage.css';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useParams } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import FbImage from '../common/components/fb_image/FbImage';
import { loadPhotosByGallery } from '../actions/photoActions';
import { getGallery } from '../actions/galleryActions';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import { getCategory } from '../actions/categoryActions';
import { trackPhotoClick, trackNavigation } from '../common/utils/analytics';
import HomeIcon from '@mui/icons-material/Home';
import SEO from '../common/components/seo/SEO';
import { getBreadcrumbStructuredData } from '../common/utils/structuredData';

const PhotosPage = () => {
    const dispatch = useDispatch();
    const photos = useSelector(state => state.photo.photosByGallery);
    const gallery = useSelector(state => state.gallery.gallery);
    const category = useSelector(state => state.category.category);

    const { galleryId } = useParams();

    useEffect(() => {
        dispatch(loadPhotosByGallery(galleryId));
        dispatch(getGallery(galleryId));
    }, [dispatch, galleryId]);

    useEffect(() => {
        if (gallery) dispatch(getCategory(gallery?.categoryId));
    }, [dispatch, gallery]);


    const structuredData = getBreadcrumbStructuredData([
        { name: 'Home', url: '/' },
        { name: 'Galleries', url: '/galleries' },
        { name: category?.categoryName || 'Category', url: category?.id ? `/galleries/${category.id}` : undefined },
        { name: gallery?.galleryName || 'Gallery', url: gallery?.id ? `/galleries/photos/${gallery.id}` : undefined },
    ]);

    return (
        <>
            <SEO
                title={gallery?.galleryName ? `${gallery.galleryName} - Landscape Photography Gallery` : 'Photo Gallery'}
                description={gallery?.galleryName 
                    ? `Browse ${gallery.galleryName} landscape photography gallery by ValeTer. ${photos?.length ? `View ${photos.length} stunning photographs.` : ''}`
                    : 'Browse landscape photography gallery.'}
                keywords={gallery?.galleryName 
                    ? `${gallery.galleryName} photography, ${gallery.galleryName} landscape photos, ${category?.categoryName || ''} photography`
                    : 'landscape photography gallery'}
                structuredData={structuredData}
            />
            <Paper className='photos-page'>
                <Breadcrumbs separator="›" aria-label="breadcrumb" className='enhanced-breadcrumbs'>
                <Link 
                    component={RouterLink}
                    underline="hover" 
                    color="inherit" 
                    to="/"
                    onClick={() => trackNavigation('Breadcrumb: Home')}
                    sx={{ display: 'flex', alignItems: 'center' }}
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
                    Home
                </Link>
                <Link 
                    component={RouterLink}
                    underline="hover" 
                    color="inherit" 
                    to="/galleries"
                    onClick={() => trackNavigation('Breadcrumb: Galleries')}
                >
                    Galleries
                </Link>
                <Link 
                    component={RouterLink}
                    underline="hover" 
                    color="inherit" 
                    to={`/galleries/${category?.id}`}
                    onClick={() => trackNavigation(`Breadcrumb: ${category?.categoryName}`)}
                >
                    {category?.categoryName}
                </Link>
                <Typography color="text.primary">{gallery?.galleryName}</Typography>
            </Breadcrumbs>
            <ImageList
                variant="masonry"
                sx={{
                    width: '100%',
                    margin: '8px',
                }}
                gap={8}
            >
                {(photos ?? []).filter(p => !p.isPanorama).map((p) => (
                    <ImageListItem key={p.id} className='photo-card'>
                        <div className='photo-card-cell'>
                            <RouterLink 
                                className='cell-content' 
                                to={`/galleries/photo/${p.id}`}
                                onClick={() => trackPhotoClick(p.id)}
                            >
                                <FbImage
                                    path={p.thumbnailPath || p.path}
                                    alt={p.photoName}
                                />
                            </RouterLink>
                        </div>
                    </ImageListItem>
                ))}
            </ImageList>
            {(photos ?? []).filter(p => !!p.isPanorama).map((p) => (
                <RouterLink 
                    key={p.id}
                    className='panorama-card' 
                    to={`/galleries/photo/${p.id}`}
                    onClick={() => trackPhotoClick(p.id)}
                >
                    <FbImage
                        path={p.thumbnailPath || p.path}
                        alt={p.photoName}
                    />
                </RouterLink>
            ))}
        </Paper>
        </>
    );
};

export default PhotosPage;
